import { IonAvatar, IonButton, IonButtons, IonCardContent, IonIcon, IonItem, IonLabel, IonList, IonSkeletonText, IonText, IonThumbnail } from '@ionic/react';
import { unwrapResult } from '@reduxjs/toolkit';
import { add, refresh } from 'ionicons/icons';
import { useEffect, useState } from 'react';
import { IRepairOrder } from '../../models/dms/repairOrder.model';
import { ITechComment } from '../../models/dms/technicianComments.model';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import { fetchTechnicianCommentsStoreAsync, setSelectedTechnicianCommentsStoreAsync } from '../../store/technicianCommentSlice';
import { IonCardWrapper } from './TechnicianCommentsCard.styles';
import AddTechnicianCommentModal from './addTechnicianCommentModal/AddTechnicianCommentModal';
import TechnicianCommentItem from './technicianCommentItem/TechnicianCommentItem';
import TechnicianCommentModal from './technicianCommentModal/TechnicianCommentModal';

interface IComponentProps {
  repairOrder: IRepairOrder;
}

const TechnicianCommentsCard: React.FC<IComponentProps> = ({ repairOrder }) => {
  const [showViewModal, setShowViewModal] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [selectedTechComment, setSelectedTechComment] = useState<ITechComment>({} as ITechComment);
  const [loadingError, setLoadingError] = useState(false);

  const { selectedTechnicianComments, selectedTechnicianCommentsLoading } = useAppSelector((state) => state.technicianComments);
  const { networkConnected } = useAppSelector((state) => state.app);

  const dispatch = useAppDispatch();

  useEffect(() => {
    loadTechComments(repairOrder);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [repairOrder.repairOrderNumber]);

  const loadTechComments = async (repairOrder: IRepairOrder) => {
    try {
      if (!repairOrder.isAdhoc && repairOrder.repairOrderNumber) {
        await dispatch(setSelectedTechnicianCommentsStoreAsync([]));
        const resultAction = await dispatch(fetchTechnicianCommentsStoreAsync(repairOrder.repairOrderNumber));
        unwrapResult(resultAction);
        setLoadingError(false);
      }
    } catch (ex) {
      setLoadingError(true);
    }
  };

  const handleClickLoadTechComments = async () => {
    await loadTechComments(repairOrder);
  };

  const handleClickComment = (techComment: ITechComment) => {
    setSelectedTechComment(techComment);
    setShowViewModal(true);
  };

  const handleClickAdd = () => {
    setShowAddModal(true);
  };

  return (
    <>
      <IonCardWrapper>
        <IonCardContent>
          <IonList>
            {/* Header */}
            <IonItem>
              <IonButtons slot="end">
                {!repairOrder.isAdhoc && (
                  <IonButton onClick={() => handleClickLoadTechComments()} fill="clear" disabled={selectedTechnicianCommentsLoading}>
                    <IonIcon slot="icon-only" icon={refresh} color="primary" />
                  </IonButton>
                )}
                <IonButton onClick={() => handleClickAdd()} fill="clear" disabled={selectedTechnicianCommentsLoading}>
                  <IonIcon slot="icon-only" icon={add} color="primary" />
                </IonButton>
              </IonButtons>
              Technician Comments
            </IonItem>

            {/* Loading Skeleton */}
            {selectedTechnicianCommentsLoading && (
              <IonItem lines="none">
                <IonAvatar slot="end" className="avatar-skeleton">
                  <IonSkeletonText animated />
                </IonAvatar>
                <IonThumbnail slot="start" className="thumbnail-skeleton">
                  <IonSkeletonText animated />
                </IonThumbnail>

                <IonLabel>
                  <IonText color="medium">
                    <IonSkeletonText style={{ width: '30%' }} animated />
                  </IonText>
                  <h2 className="ion-text-capitalize">
                    <IonSkeletonText animated style={{ width: '50%' }} />
                  </h2>
                  <IonLabel>
                    <IonSkeletonText animated style={{ width: '90%' }} />
                  </IonLabel>
                </IonLabel>
              </IonItem>
            )}

            {/* Data */}
            {selectedTechnicianComments.map((techComment, index) => {
              return <TechnicianCommentItem key={index} techComment={techComment} onClick={() => handleClickComment(techComment)} />;
            })}

            {/* Error */}
            {loadingError && (
              <IonItem lines="none">
                <IonText color="danger"></IonText>
                {networkConnected && <h4>Failed to load technician comments</h4>}
                {!networkConnected && <h4>Comments not available in offline mode</h4>}
              </IonItem>
            )}
          </IonList>
        </IonCardContent>
      </IonCardWrapper>
      <TechnicianCommentModal showModal={showViewModal} techComment={selectedTechComment} handleDismiss={() => setShowViewModal(false)} />
      <AddTechnicianCommentModal showModal={showAddModal} handleDismiss={() => setShowAddModal(false)} />
    </>
  );
};

export default TechnicianCommentsCard;
